import { IResourceColumn } from '~/client/src/desktop/components/Resource/types'
import {
  DATE_CLOSURES_KEY,
  HOURS_OF_OPERATION_KEY,
  MAX_BOOKING_DURATION_KEY,
  MAX_OVERLAPPING_KEY,
  PERMITTED_COMPANIES_KEY,
  RESOURCE_CONTROL_KEYS,
  SHOW_IN_MAP_KEY,
  WORKFLOW_RESOURCE_KEY,
  WORKFLOW_TYPES_KEY,
} from '~/client/src/shared/components/ResourceControls/controlKeys'
import Localization from '~/client/src/shared/localization/LocalizationManager'

// The control keys now live in shared controlKeys; re-export them so existing
// desktop consumers that import the keys from this module keep working.
export {
  DATE_CLOSURES_KEY,
  HOURS_OF_OPERATION_KEY,
  MAX_BOOKING_DURATION_KEY,
  MAX_OVERLAPPING_KEY,
  PERMITTED_COMPANIES_KEY,
  RESOURCE_CONTROL_KEYS,
  SHOW_IN_MAP_KEY,
  WORKFLOW_RESOURCE_KEY,
  WORKFLOW_TYPES_KEY,
}

export const SELECT_COLUMN_KEY = '__select'
export const DELETE_COLUMN_KEY = '__delete'

// ID and Name lead the table and stay frozen, the way they do on the Objects tab
// (see EQUIPMENT_FROZEN_COLUMNS / MATERIAL_FROZEN_COLUMNS — Equipment also freezes
// its Icon column there, which the Controls tab doesn't show). Module-level so the
// identity stays stable — useColumnConfig memoizes its defaults on it.
export const FROZEN_COLUMN_KEYS = ['stxResourceId', 'name']

const CONTROL_COLUMN_KEY_SET = new Set<string>(RESOURCE_CONTROL_KEYS)

export function getResourceControlsColumns(
  controls: readonly string[],
): IResourceColumn[] {
  const enabledControls = new Set(controls)
  const columns: IResourceColumn[] = [
    {
      dataKey: 'stxResourceId',
      label: Localization.translator.id,
      width: 130,
      accessorFn: record => record.stxResourceId ?? '',
    },
    {
      dataKey: 'name',
      label: Localization.translator.name,
      width: 240,
      accessorFn: record => record.name,
    },
    {
      dataKey: WORKFLOW_TYPES_KEY,
      label: Localization.translator.workflowTypes,
      width: 360,
      // The cell renders from the fields' own allow-lists; this exists only so the column
      // registers with a sortable accessor, as DATE_CLOSURES_KEY does.
      accessorFn: () => [],
    },
    {
      dataKey: PERMITTED_COMPANIES_KEY,
      label: Localization.translator.permittedCompanies,
      width: 360,
      accessorFn: record => record.root?.permittedCompanies ?? [],
    },
    {
      dataKey: HOURS_OF_OPERATION_KEY,
      label: Localization.translator.hoursOfOperation,
      // A dated window spells both ends out — "Sep 04, 10:00 AM - Sep 04, 09:00
      // PM" — which overran 280 and pushed its dismiss button past the column
      // edge, where it could not be clicked.
      width: 360,
      accessorFn: record => record.root?.operatingIntervals ?? [],
    },
    {
      dataKey: DATE_CLOSURES_KEY,
      label: Localization.translator.dateClosures,
      width: 280,
      // Closures aren't part of the resource GraphQL record — they live in the
      // global ClosuresStore keyed by locationId. DateClosuresCell reads them
      // from the store by record id; this accessor exists only so the column
      // registers and participates in column config.
      accessorFn: () => [],
    },
    {
      dataKey: MAX_BOOKING_DURATION_KEY,
      label: `${Localization.translator.maxBookingDuration} (${Localization.translator.hoursUnit})`,
      width: 280,
      accessorFn: record =>
        record.root?.workflowRestrictions?.maxBookingDurations ?? [],
    },
    {
      dataKey: MAX_OVERLAPPING_KEY,
      label: Localization.translator.maxOverlapping,
      width: 280,
      accessorFn: record =>
        record.root?.workflowRestrictions?.maxOverlappingBookings ?? [],
    },
    // Show in Maps, WR (isBookable) and Delete are structural, always pinned
    // right and absent from the Display config — see ResourceControlsTable.
  ]

  return columns.filter(
    column =>
      !CONTROL_COLUMN_KEY_SET.has(column.dataKey) ||
      enabledControls.has(column.dataKey),
  )
}
